"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { HiOutlineDocumentText, HiOutlineDownload, HiOutlinePhotograph } from "react-icons/hi";
import Button from "@/components/ui/button";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { EmailAttachment } from "@/types/email";
import { emailQueryKeys, emailQueryOptions } from "@/api/provider/comms/email/email-query";
interface EmailAttachmentsProps {
  attachments: EmailAttachment[];
  className?: string;
}
function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}
function getFileIcon(mimeType: string | null) {
  if (mimeType?.startsWith("image/")) {
    return HiOutlinePhotograph;
  }
  return HiOutlineDocumentText;
}
function truncateFilename(filename: string, maxLength: number = 30): string {
  if (filename.length <= maxLength) return filename;
  const ext = filename.lastIndexOf(".");
  if (ext === -1) {
    return filename.slice(0, maxLength - 3) + "...";
  }
  const extension = filename.slice(ext);
  const name = filename.slice(0, ext);
  const availableLength = maxLength - extension.length - 3;
  if (availableLength <= 0) {
    return filename.slice(0, maxLength - 3) + "...";
  }
  return name.slice(0, availableLength) + "..." + extension;
}
export function EmailAttachments({
  attachments,
  className
}: EmailAttachmentsProps) {
  const queryClient = useQueryClient();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Filter out inline attachments (images embedded in email body)
  const nonInlineAttachments = attachments.filter(a => !a.isInline);
  if (nonInlineAttachments.length === 0) {
    return null;
  }
  const handleDownload = async (attachment: EmailAttachment) => {
    setDownloadingId(attachment.id);
    try {
      // Check cache first (primed by bulk endpoint), then fetch if needed
      let signedUrl = queryClient.getQueryData<string | null>(emailQueryKeys.attachmentDownload(attachment.id));
      if (!signedUrl) {
        // Fetch and cache for future use
        signedUrl = await queryClient.fetchQuery(emailQueryOptions.attachmentDownload(attachment.id));
      }
      if (signedUrl) {
        // Create a temporary link and trigger download
        const link = document.createElement("a");
        link.href = signedUrl;
        link.download = attachment.filename;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Failed to download attachment:", error);
      toast({
        variant: "error",
        title: "Download failed",
        description: "Unable to download the attachment. Please try again."
      });
    } finally {
      setDownloadingId(null);
    }
  };
  return <div className={cx("space-y-2", className)}>
      <div className="text-caption-strong text-foreground-secondary uppercase tracking-wide">
        Attachments ({nonInlineAttachments.length})
      </div>
      <div className="flex gap-2">
        {nonInlineAttachments.map(attachment_0 => {
        const FileIcon = getFileIcon(attachment_0.mimeType);
        const fileSize = formatFileSize(attachment_0.sizeBytes);
        const isDownloading = downloadingId === attachment_0.id;
        return <div key={attachment_0.id} className={cx("flex items-center gap-2 p-2 rounded-lg", "bg-background-secondary border border-border/50", "group transition-colors hover:border-border")}>
              <div className="shrink-0 p-1.5 rounded bg-background">
                <FileIcon className="h-4 w-4 text-foreground-secondary" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-body-strong text-foreground truncate" title={attachment_0.filename}>
                  {truncateFilename(attachment_0.filename)}
                </div>
                {fileSize && <div className="text-caption text-foreground-muted">
                    {fileSize}
                  </div>}
              </div>

              <Button variant="ghost" size="icon-sm" className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" tooltip="Download" onClick={() => handleDownload(attachment_0)} disabled={isDownloading} isLoading={isDownloading}>
                <HiOutlineDownload className="h-4 w-4" />
              </Button>
            </div>;
      })}
      </div>
    </div>;
}